import React from "react";
import { ArrowRight, CheckCircle2, Clock3, FileText, Send, X } from "lucide-react";
import Modal from "../../../../../components/ui/Modal";
import Button from "../../../../../components/ui/Button";
import { useAppDispatch, useAppSelector } from "../../../../../app/hooks";
import { remittanceActions } from "../../../state/remittanceSlice";
import { formatQuoteDateTime, formatQuoteMoney } from "../../../utils/avanseQuote";
import { QuoteDetailItem } from "../../../types/remittance/remitter/quoteDetails.types";
import { QuoteRadio } from "./QuoteRadio";

interface SelectQuoteModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  onProceedWithout: () => void;
}

export const SelectQuoteModal: React.FC<SelectQuoteModalProps> = ({
  open,
  onClose,
  onConfirm,
  onProceedWithout,
}) => {
  const dispatch = useAppDispatch();
  const { quoteDetails, selectedQuoteUniqueKey } = useAppSelector(
    (state) => state.remittance.remitter
  );

  const canSubmit = Boolean(selectedQuoteUniqueKey);

  const handleSelectQuote = (quote: QuoteDetailItem) => {
    if (!quote.quoteVerified) return;
    dispatch(remittanceActions.setSelectedQuoteUniqueKey(quote.uniqueKey));
  };

  return (
    <Modal
      visible={open}
      closeHandler={onClose}
      closable={false}
      maskClosable={false}
      modalClass="!p-0 rounded-2xl !w-[min(820px,95vw)] !max-w-[820px]"
      headerClass="hidden"
    >
      <div className="relative flex max-h-[90vh] flex-col overflow-hidden rounded-2xl bg-white">
        <div className="border-b border-[#F0F0F0] px-8 pb-5 pt-8">
          <button
            type="button"
            onClick={onClose}
            className="absolute top-6 right-6 flex h-9 w-9 items-center justify-center rounded-full text-[#666] transition-colors hover:bg-[#F5F5F5] hover:text-[#111]"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>

          <h2 className="pr-12 text-[32px] font-bold leading-tight text-[#111] font-inter">
            Quotes
          </h2>
          <p className="mt-2 text-sm leading-relaxed text-[#666] font-inter">
            Select a verified quote, then submit — or proceed without quote confirmation.
          </p>
        </div>

        <div className="min-h-[240px] flex-1 space-y-3 overflow-y-auto bg-[#FAF7F2] px-8 py-5 scrollbar scrollbar-thumb">
          {quoteDetails.loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="h-8 w-8 animate-spin rounded-full border-2 border-[#2B63E1] border-t-transparent" />
            </div>
          ) : null}

          {!quoteDetails.loading && quoteDetails.error ? (
            <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {quoteDetails.error}
            </div>
          ) : null}

          {!quoteDetails.loading && !quoteDetails.error && quoteDetails.list.length === 0 ? (
            <div className="rounded-xl border border-[#E8E8E8] bg-white px-4 py-10 text-center text-sm text-[#666]">
              No quotes found for this remitter.
            </div>
          ) : null}

          {!quoteDetails.loading &&
            quoteDetails.list.map((quote) => {
              const isVerified = quote.quoteVerified;
              const isSelected = selectedQuoteUniqueKey === quote.uniqueKey;

              return (
                <div
                  key={quote.uniqueKey}
                  role={isVerified ? "button" : undefined}
                  tabIndex={isVerified ? 0 : undefined}
                  onClick={() => handleSelectQuote(quote)}
                  onKeyDown={(event) => {
                    if (isVerified && (event.key === "Enter" || event.key === " ")) {
                      event.preventDefault();
                      handleSelectQuote(quote);
                    }
                  }}
                  className={`rounded-xl border bg-white px-4 py-4 transition-all ${
                    isSelected ? "border-[#2B63E1] shadow-sm" : "border-[#E5E5E5]"
                  } ${isVerified ? "cursor-pointer hover:border-[#93B8F8]" : ""}`}
                >
                  <div className="flex items-start gap-4">
                    <div
                      className="flex w-6 shrink-0 items-start justify-center pt-1"
                      onClick={(event) => event.stopPropagation()}
                    >
                      {isVerified ? (
                        <QuoteRadio
                          value={quote.uniqueKey}
                          checked={isSelected}
                          onChange={() => handleSelectQuote(quote)}
                        />
                      ) : null}
                    </div>

                    <div
                      className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${
                        isVerified ? "bg-[#E8F8EF]" : "bg-[#FFF4E5]"
                      }`}
                    >
                      <FileText
                        className={`h-5 w-5 ${isVerified ? "text-[#1F8A4C]" : "text-[#C77700]"}`}
                      />
                    </div>

                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                        {isVerified ? (
                          <span className="inline-flex items-center gap-1.5 rounded-full bg-[#E8F8EF] px-2.5 py-1 text-xs font-semibold text-[#1F8A4C]">
                            <CheckCircle2 className="h-3.5 w-3.5" />
                            Verified
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 rounded-full bg-[#FFF4E5] px-2.5 py-1 text-xs font-semibold text-[#C77700]">
                            <Clock3 className="h-3.5 w-3.5" />
                            Pending
                          </span>
                        )}

                        <p className="text-sm text-[#444]">
                          You send{" "}
                          <span className="font-bold text-[#111]">
                            {formatQuoteMoney(quote.sendCurrency, quote.userSend)}
                          </span>
                        </p>

                        <p className="text-sm text-[#444]">
                          Recipient receive{" "}
                          <span className="font-bold text-[#111]">
                            {formatQuoteMoney(quote.receiveCurrency, quote.recipientReceive)}
                          </span>
                        </p>
                      </div>

                      <div className="mt-2.5 flex flex-wrap items-center gap-x-5 gap-y-1 text-xs text-[#666]">
                        <span className="inline-flex items-center gap-1.5">
                          <Send className="h-3.5 w-3.5 shrink-0 text-[#999]" />
                          Sent {formatQuoteDateTime(quote.createdOn)}
                        </span>

                        {isVerified ? (
                          <span className="inline-flex items-center gap-1.5 text-[#1F8A4C]">
                            <CheckCircle2 className="h-3.5 w-3.5 shrink-0" />
                            Verified {formatQuoteDateTime(quote.verifiedOn)}
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 text-[#888]">
                            <Clock3 className="h-3.5 w-3.5 shrink-0" />
                            Awaiting verification
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
        </div>

        <div className="space-y-3 border-t border-[#F0F0F0] bg-white px-8 py-6">
          <button
            type="button"
            onClick={onProceedWithout}
            className="flex w-full items-center justify-center gap-2 rounded-xl border border-dashed border-[#D4C9BC] bg-[#FAF7F2] px-4 py-3.5 text-sm font-semibold text-[#333] transition-colors hover:bg-[#F3EBE0]"
          >
            Proceed without quote confirmation
            <ArrowRight className="h-4 w-4" />
          </button>

          <Button
            bgColor="bg-[#2B63E1]"
            fontStyle={{
              fontFamily: "font-inter",
              fontSize: "text-base",
              fontWeight: "font-semibold",
              fontColor: "text-white",
            }}
            borderStyle={{ borderRadius: "rounded-xl" }}
            className={`h-[52px] w-full ${!canSubmit ? "opacity-50" : ""}`}
            disabled={!canSubmit}
            onClickHandler={onConfirm}
          >
            Submit selected quote
          </Button>
        </div>
      </div>
    </Modal>
  );
};
